import {
  Response as ClientResponse,
  ProviderConfig,
  ImplementationDetail,
} from '@finch-api/common/dist/internal/connect/validate';
import LoaderButton from 'components/Button/LoaderButton';
import { updatedInstructions, getInstructionsUrl } from './instructions';
import { ConnectError, StateRedirect } from '../types';
import SignInBase from '../SignIn/SignInBase';
import MainContainer from '../SignIn/SignInMainContainer';
import InstructionsCheckbox from 'components/Input/InstructionsCheckboxInput';
import { ProviderAppShell } from '../ProviderAppShell';
import { useEffect, useRef } from 'react';
import { useTrackConversionStep } from '../../../services/track-conversion';
import { useTrackConversionError } from 'services/track-conversion';
import { getNameParts } from './utils';
import { getManualContactName } from 'utils/connect-branding';

export const FINCH_DEFAULT_PHONE = '(917) 717-3974';

export type ManualSignInInstructionProps = {
  client: ClientResponse;
  payrollProvider: ProviderConfig;
  contactEmailInContext?: string;
  companyNameInContext?: string;
  companyName?: string;
  clientName?: string;
  onSubmit: () => Promise<void>;
  onPrevStep?: () => void;
  handleStateRedirect: (redirect: StateRedirect) => void;
  providerImplementation: ImplementationDetail;
  error: ConnectError | null;
  setError: (error: ConnectError | null) => void;
};

const DefaultSignInManualInstruction = ({
  client,
  payrollProvider,
  contactEmailInContext,
  companyNameInContext,
  companyName,
  clientName,
  onSubmit,
  onPrevStep,
  handleStateRedirect,
  providerImplementation,
  error,
  setError,
}: ManualSignInInstructionProps) => {
  const trackConversion = useTrackConversionStep();
  const mounted = useRef(false);
  const trackConversionError = useTrackConversionError();

  useEffect(() => {
    if (!mounted.current) {
      trackConversion({
        action: 'manual_instructions_displayed',
      });
      mounted.current = true;
    }
  }, [trackConversion]);

  const isXeroUSProvider = payrollProvider.id === 'xero_us';
  const manualContactName = getManualContactName(client, clientName);

  const { firstName, lastName, fullName } = getNameParts(client, clientName);
  const details = {
    accountantEmail:
      payrollProvider?.sessionAccountantEmail ||
      client?.manualConfig?.email ||
      payrollProvider?.defaultAccountantEmail,
    username: client?.manualConfig?.name || manualContactName,
    userId:
      client?.manualConfig?.userId ||
      client?.manualConfig?.name ||
      manualContactName,
    firstName,
    lastName,
    fullName,
    ssn: client?.manualConfig?.ssn,
    phoneNumber: client?.manualConfig?.phoneNumber || FINCH_DEFAULT_PHONE,
    companyName: companyName || companyNameInContext || client?.name,
  };
  const instructionsLink = getInstructionsUrl(payrollProvider.id);
  const instructions = updatedInstructions(payrollProvider, client, details, {
    detailsOnly: true,
  });

  const onRedirect = () => {
    handleStateRedirect({
      next: 'SIGN_IN',
      nextContext: {
        redirect: true,
        payrollProviderId: 'gusto',
      },
    });
  };

  return (
    <>
      <ProviderAppShell
        provider={payrollProvider}
        error={error}
        providerImplementation={providerImplementation}
        setError={setError}
        headerText={`Connect your ${payrollProvider.displayName} account`}
        goBack={
          !(companyNameInContext && contactEmailInContext)
            ? onPrevStep
            : undefined
        }
      >
        <SignInBase
          formLabel="Manual Sign In Form"
          spacing="md"
          instructions={instructions}
          instructionsLink={instructionsLink}
          instructionsCalloutAppName={details.username}
          instructionsCalloutProviderDisplayName={payrollProvider.displayName}
          fieldNames={['instructionCheck']}
          onSubmit={() => {
            trackConversion({ action: 'manual_instructions_submitted' });
            if (isXeroUSProvider) {
              onRedirect();
            } else {
              onSubmit();
            }
          }}
          render={({ instructionCheck }) => {
            return (
              <>
                <MainContainer useNewDesign>
                  {!isXeroUSProvider && (
                    <InstructionsCheckbox
                      checked={instructionCheck.value}
                      onChange={(checked) => instructionCheck.onChange(checked)}
                      label="I've completed these steps"
                    />
                  )}
                </MainContainer>
                <LoaderButton
                  type="submit"
                  disabled={!instructionCheck.value}
                  text={isXeroUSProvider ? 'Redirect' : 'Connect'}
                />
              </>
            );
          }}
        />
      </ProviderAppShell>
    </>
  );
};

export default DefaultSignInManualInstruction;
